import { Sprite, Texture, type Container } from 'pixi.js';
import type { TownData } from '@/modules/game-domain/world.types';
import type { ReadonlyData } from '@/modules/game-domain/render-snapshot';
import { worldToScreen } from './coordinates';
import { meshyNpcAnchor, meshyNpcClip, meshyNpcFrame, meshyNpcModel } from './meshy-npc-clips';
import type { MeshyNpcAssets, MeshyNpcAtlasRequest } from './meshy-npc-assets';
import { npcWorldFacing, sampleAmbientNpcPlan } from './npc-presentation';
import type { BodyRect } from './actor-visibility';

/** Draw only a projection of saved inhabitants and server schedules; never adds interactive actors. */
export class AmbientInhabitants {
  readonly entries = new Map<string, Sprite>();
  constructor(private readonly layer: Container, private readonly assets: Pick<MeshyNpcAssets, 'get' | 'preload'>) {}

  async preload(towns: readonly ReadonlyData<TownData>[], serverNowMs = Date.now()): Promise<void> {
    const requests: MeshyNpcAtlasRequest[] = [];
    for (const town of towns) for (const npc of town.ambientNPCs ?? []) {
      const model = meshyNpcModel(npc.appearanceKey);
      if (!model) continue;
      const plan = town.npcPresentation?.ambient[npc.id];
      const sample = plan ? sampleAmbientNpcPlan(plan, town.npcPresentation!.epochUnixMs, serverNowMs, !!town.npcPresentation?.runtime) : null;
      requests.push({ model, clip: sample?.clip ?? 'catwalk', facing: npcWorldFacing(sample?.dx ?? 0, sample?.dy ?? 0) });
    }
    await this.assets.preload(requests);
  }

  draw(towns: readonly ReadonlyData<TownData>[], now: number, bounds?: BodyRect, serverNowMs = Date.now()): void {
    const present = new Set<string>();
    for (const sprite of this.entries.values()) sprite.visible = false;
    for (const town of towns) for (const inhabitant of town.ambientNPCs ?? []) {
      const key = `ambient:${town.id}:${inhabitant.id}`;
      present.add(key);
      const model = meshyNpcModel(inhabitant.appearanceKey);
      if (!model) continue;
      const plan = town.npcPresentation?.ambient[inhabitant.id];
      const sample = plan ? sampleAmbientNpcPlan(plan, town.npcPresentation!.epochUnixMs, serverNowMs, !!town.npcPresentation?.runtime) : null;
      const point = worldToScreen(sample ? town.centerX + sample.x : inhabitant.worldX,
        sample ? town.centerY + sample.y : inhabitant.worldY);
      if (bounds && (point.x < bounds.left || point.x > bounds.right || point.y < bounds.top || point.y > bounds.bottom)) continue;
      const clip = sample?.clip ?? 'catwalk', facing = npcWorldFacing(sample?.dx ?? 0, sample?.dy ?? 0);
      const definition = meshyNpcClip(model, clip, facing);
      const frames = this.assets.get(model, clip, facing);
      const previous = this.entries.get(key)?.texture;
      const texture = frames?.[meshyNpcFrame(definition, sample?.elapsedMs ?? now)]
        ?? (previous && !previous.destroyed && previous !== Texture.EMPTY ? previous : null);
      if (!texture) continue;
      let sprite = this.entries.get(key);
      if (!sprite) {
        sprite = new Sprite(texture); sprite.label = key; sprite.eventMode = 'none';
        this.entries.set(key, sprite); this.layer.addChild(sprite);
      }
      sprite.texture = texture;
      sprite.position.set(point.x, point.y);
      const anchor = meshyNpcAnchor(definition);
      sprite.anchor.set(anchor.x, anchor.y);
      sprite.scale.set(1); // Child75%stature is baked; a second scale would shrink them twice.
      sprite.zIndex = point.y; sprite.visible = true;
    }
    for (const [key, sprite] of this.entries) {
      if (!present.has(key)) { sprite.destroy(); this.entries.delete(key); }
      else if (!sprite.visible) sprite.texture = Texture.EMPTY;
    }
  }
  destroy(): void { for (const sprite of this.entries.values()) sprite.destroy(); this.entries.clear(); }
}
